import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useGetSimulationCollectionByIdQuery, useGetSimulationsQuery } from "../../store/services/simulation-service";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    Alert,
    Button
} from "@mui/material";
import MainCard from "../../components/cards/MainCard";
import { useTheme, styled } from "@mui/material/styles";
import EmailIcon from '@mui/icons-material/Email';
import CategoryIcon from '@mui/icons-material/Category';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import Rating from "@mui/material/Rating";
import { getCardColor, getTextColor } from '../../utils/colorUtils';
import { CalendarSimulationPreviewModal } from "../delivery/components/modals/CalendarSimulationPreview";
import SharingDisplay from "../../components/ContentSharing/SharingDisplay";
import StyledChip from '../../components/Chips/StyledChip';
import PhishingIcon from '@mui/icons-material/Phishing';
import { useModal } from '../../components/Modal/ModalContext';


// Styled components for proper scrolling behavior
const CollectionContainer = styled(Box)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(3),
}));

const HeaderSection = styled(Box)(({ theme }) => ({
    flexShrink: 0,
    marginBottom: theme.spacing(3),
}));

const SimulationsListContainer = styled(MainCard)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.background.paper,
    height: 'auto',
    maxHeight: 'none',
}));

const StyledList = styled(List)(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(0, 2),
    marginTop: theme.spacing(-2),
    overflow: 'visible',
}));

const SimulationListItem = styled(ListItem)(({ theme }) => ({
    alignItems: 'flex-start',
    padding: theme.spacing(2, 0),
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: '200px',
}));

const ActionButton = styled(Button)(({ theme }) => ({
    minWidth: '100px',
    ...(theme.palette.mode !== 'light' && {
        backgroundColor: theme.palette.primary.main,
        color: getTextColor(theme.palette.primary.main),
        '&:hover': {
            backgroundColor: "#69686b",
        },
    }),
}));

SimulationCollectionPreview.propTypes = {
    collectionId: PropTypes.string.isRequired,
    setSimulationPreviewOpen: PropTypes.func,
    onNestedSimulationPreview: PropTypes.func,
    context: PropTypes.string
};

function SimulationCollectionPreviewContent({
    collectionId,
    setSimulationPreviewOpen,
    onNestedSimulationPreview,
    context = 'general'
}) {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const modalContext = useModal();

    const { tenancyId } = useSelector((state) => state.tenancy);

    // Scope the simulation lookup to the requesting tenancy, matching SimulationCollectionForm.
    // Without tenancyId the API treats the caller as tenancy 0 and returns only globally shared
    // simulations, so a tenancy's own custom simulations would be filtered out of the preview.
    const user = JSON.parse(localStorage.getItem("user"));
    const additionalQueryParams = user?.role === "Administrators"
        ? ""
        : `&published=true&tenancyId=${tenancyId}`;

    // Preview modal state
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [selectedSimulationId, setSelectedSimulationId] = useState(null);

    const isTableContext = typeof setSimulationPreviewOpen === 'function';
    const isCalendarContext = context === 'calendar' || !!onNestedSimulationPreview;

    const {
        data: collection,
        isLoading: collectionLoading,
        error: collectionError
    } = useGetSimulationCollectionByIdQuery(collectionId);

    const {
        data: allSimulations,
        isLoading: simulationsLoading
    } = useGetSimulationsQuery({
        perPage: 1000,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: additionalQueryParams
    });

    const handlePreview = useCallback((simulation) => {
        if (isCalendarContext && onNestedSimulationPreview) {
            // Calendar context: use callback
            onNestedSimulationPreview(simulation.id);
        } else {
            // Table context: use existing modal system
            setSelectedSimulationId(simulation.id);
            if (modalContext.setActiveModal) {
                modalContext.setActiveModal('simulation');
            }
            setPreviewModalOpen(true);
            if (setSimulationPreviewOpen) {
                setSimulationPreviewOpen(true);
            }
        }
    }, [onNestedSimulationPreview, isCalendarContext, modalContext, setSimulationPreviewOpen]);

    // close handler with modal management
    const handleClosePreview = useCallback(() => {
        setPreviewModalOpen(false);
        setSelectedSimulationId(null);

        if (isTableContext) {
            // Table context: clear activeModal and use visibility callback
            if (modalContext.setActiveModal) {
                modalContext.setActiveModal(null);
            }
            if (setSimulationPreviewOpen) {
                setSimulationPreviewOpen(false);
            }
        } else if (!isCalendarContext) {
            // General context: restore collection modal state
            if (modalContext.setActiveModal) {
                modalContext.setActiveModal('collection');
            }
        }
    }, [isTableContext, isCalendarContext, modalContext, setSimulationPreviewOpen]);

    if (collectionLoading || simulationsLoading) {
        return (
            <LoadingContainer>
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>
                    Loading collection details...
                </Typography>
            </LoadingContainer>
        );
    }

    if (collectionError) {
        return (
            <CollectionContainer>
                <Box sx={{ p: 4 }}>
                    <MainCard>
                        <Alert severity="error">Failed to load collection details</Alert>
                    </MainCard>
                </Box>
            </CollectionContainer>
        );
    }

    // Filter simulations that are in this collection
    const collectionSimulations = allSimulations?.filter(sim =>
        collection?.simulationIds?.includes(sim.id)
    ) || [];

    return (
        <CollectionContainer>
            <ScrollableContent>
                {/* Header Section */}
                <HeaderSection>
                    <MainCard sx={{ bgcolor: getCardColor(lightMode) }}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h3" gutterBottom>
                                {collection?.collectionName}
                            </Typography>

                            {/* Additional Info Chips */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                <StyledChip
                                    label={`${collectionSimulations.length} Simulations`}
                                    size="small"
                                    color={theme.palette.secondary.main}
                                    icon={<PhishingIcon />}
                                    variant="filled"
                                />
                            </Box>

                            {/* Collection Info */}
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        <strong>Created:</strong> {new Date(collection?.dateCreated).toLocaleDateString()}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        <strong>Last Updated:</strong> {new Date(collection?.dateUpdated).toLocaleDateString()}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </MainCard>
                </HeaderSection>

                {/* Sharing and Status Display */}
                <Box sx={{ mb: 2 }}>
                    <SharingDisplay
                        published={collection?.published}
                        sharedTenancyIds={collection?.sharedTenancyIds}
                        publishToFamily={collection?.publishToFamily}
                        variant="detailed"
                    />
                </Box>

                {/* Simulations List */}
                <SimulationsListContainer>
                    {collectionSimulations.length === 0 ? (
                        <Box sx={{ p: 3 }}>
                            <Alert severity="info">
                                This collection doesn't contain any simulations yet.
                            </Alert>
                        </Box>
                    ) : (
                        <StyledList>
                            {collectionSimulations.map((simulation, index) => (
                                <React.Fragment key={simulation.id}>
                                    <SimulationListItem>
                                        <ListItemText
                                            primary={
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 1,
                                                    flexWrap: 'wrap'
                                                }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            wordBreak: 'break-word',
                                                            flex: '0 1 auto',
                                                            minWidth: 0
                                                        }}
                                                    >
                                                        {simulation.simulationName}
                                                    </Typography>
                                                    <StyledChip
                                                        label={simulation.simulationType}
                                                        size="small"
                                                        icon={<CategoryIcon />}
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        mb: 1,
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <EmailIcon fontSize="small" color="action" />
                                                            <Typography
                                                                variant="body2"
                                                                color="textSecondary"
                                                                sx={{ wordBreak: 'break-all' }}
                                                            >
                                                                {simulation.emailFromAddress}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Difficulty:
                                                            </Typography>
                                                            <Rating
                                                                name="difficulty-rating"
                                                                value={simulation.difficultyRating}
                                                                max={3}
                                                                readOnly
                                                                size="small"
                                                                icon={<StarRoundedIcon fontSize="inherit" />}
                                                                emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                                                            />
                                                        </Box>
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        sx={{ mb: 1, wordBreak: 'break-word' }}
                                                    >
                                                        <strong>Subject:</strong> {simulation.emailSubject || "No subject"}
                                                    </Typography>
                                                    {simulation.linkedUniversityCourseId && (
                                                        <Typography variant="body2" color="textSecondary">
                                                            <strong>Linked Course:</strong> {simulation.linkedUniversityCourseId}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                        />
                                        <Box sx={{
                                            ml: 2,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            flexShrink: 0,
                                            gap: 1,
                                            flexDirection: 'row'
                                        }}>
                                            <ActionButton
                                                variant="contained"
                                                color={lightMode ? "warning" : "primary"}
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePreview(simulation);
                                                }}
                                            >
                                                Preview
                                            </ActionButton>
                                        </Box>
                                    </SimulationListItem>
                                    {index < collectionSimulations.length - 1 && (
                                        <Divider sx={{ mx: 0 }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </StyledList>
                    )}
                </SimulationsListContainer>
            </ScrollableContent>

            {/* Individual Simulation Preview Modal - only show if not in calendar context */}
            {selectedSimulationId && !isCalendarContext && (
                <CalendarSimulationPreviewModal
                    simulationId={selectedSimulationId}
                    open={modalContext.activeModal === 'simulation' && previewModalOpen}
                    onClose={handleClosePreview}
                    isStandalone={false}
                />
            )}
        </CollectionContainer>
    );
}

export default function SimulationCollectionPreview({
    collectionId,
    setSimulationPreviewOpen,
    onNestedSimulationPreview,
    context = 'general'
}) {
    return (
        <SimulationCollectionPreviewContent
            collectionId={collectionId}
            setSimulationPreviewOpen={setSimulationPreviewOpen}
            onNestedSimulationPreview={onNestedSimulationPreview}
            context={context}
        />
    );
}